import { useEffect, useState } from "react";
import { PublicDrops } from "../components/PublicDrops";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShoppingCart, Loader2, Video, Shirt, Sparkles, Star, Copy, Flame } from "lucide-react";
import MascotBand from "../components/MascotBand";
import { MemberStoreBanner, MemberRedirectDialog, isPhysicalProduct } from "../components/store/MemberRedirect";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const TYPE_ICON = { physical: Shirt, course: Video, subscription: Video, service: Sparkles };

// Bannière été : visible seulement jusqu'à la date d'expiration du code RECONQUETE15 (1er sept. 2026)
const SUMMER_PROMO_ACTIVE = new Date() < new Date("2026-09-01T00:00:00");

// Ambassadeur du mois : chargé depuis /api/public/featured-student/current (change automatiquement chaque mois)

export default function BoutiqueDropship() {
  const [FEATURED_STUDENT, setFeatured] = useState(null);
  useEffect(() => { fetch(`${API}/public/featured-student/current`).then((r) => r.json()).then((d) => setFeatured(d.featured || null)).catch(() => {}); }, []);
  const FEATURED_ACTIVE = !!FEATURED_STUDENT;
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({});
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeBrand, setActiveBrand] = useState("all");
  const [limitedOnly, setLimitedOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [promoCode, setPromoCode] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("promo")?.trim().toUpperCase() || ""; }
    catch { return ""; }
  });
  const [brokenImgs, setBrokenImgs] = useState({});
  const [multiDisc, setMultiDisc] = useState(null);

  // Vérifie l'éligibilité au rabais multi-disciplines (-25% équipement) quand le courriel change
  useEffect(() => {
    const e = (email || "").trim();
    const t = setTimeout(async () => {
      if (!e || !e.includes("@")) { setMultiDisc(null); return; }
      try {
        const { data } = await axios.get(`${API}/dropship/multi-discipline-check`, { params: { email: e } });
        setMultiDisc(data?.eligible ? data : null);
      } catch { setMultiDisc(null); }
    }, 600);
    return () => clearTimeout(t);
  }, [email]);

  useEffect(() => {
    document.title = "Boutique Institut Impact Élite Martial";
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/dropship/products`);
        if (!cancelled) setProducts(data);
      } catch { if (!cancelled) toast.error("Erreur de chargement"); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const [redirectProduct, setRedirectProduct] = useState(null);
  const addToCart = (p) => {
    if (isPhysicalProduct(p)) { setRedirectProduct(p); return; }
    setCart((c) => ({ ...c, [p.id]: (c[p.id] || 0) + 1 }));
    toast.success(`${p.name} ajouté au panier`);
  };
  const removeFromCart = (id) => setCart((c) => { const n = { ...c }; delete n[id]; return n; });
  const cartCount = Object.values(cart).reduce((a, b) => a + b, 0);
  const cartItems = products.filter((p) => cart[p.id]);
  const subtotal = cartItems.reduce((s, p) => s + p.price_cad * cart[p.id], 0);
  const promoPct = (promoCode || "").trim().toUpperCase() === "MAELLE10" ? 10
                  : (promoCode || "").trim().toUpperCase() === "DOJOVAL10" ? 10
                  : (promoCode || "").trim().toUpperCase() === "RECONQUETE15" ? 15
                  : 0;
  const discount = +(subtotal * promoPct / 100).toFixed(2);
  const afterDiscount = +(subtotal - discount).toFixed(2);
  const gst = +(afterDiscount * 0.05).toFixed(2);
  const qst = +(afterDiscount * 0.09975).toFixed(2);
  const total = +(afterDiscount + gst + qst).toFixed(2);

  const checkout = async () => {
    if (!email) return toast.error("Email obligatoire");
    if (cartCount === 0) return toast.error("Panier vide");
    setCheckingOut(true);
    try {
      const items = Object.entries(cart).map(([product_id, quantity]) => ({ product_id, quantity }));
      const { data } = await axios.post(`${API}/dropship/checkout`, {
        items, customer_email: email, customer_name: name,
        promo_code: promoCode || undefined,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur paiement");
    } finally { setCheckingOut(false); }
  };

  if (loading) return <div className="min-h-screen bg-zinc-950 text-zinc-400 flex items-center justify-center"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-black to-zinc-900 text-white pb-32" data-testid="boutique-dropship">
      <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center font-bold">II</div>
            <div>
              <div className="font-bold text-sm">Institut Impact</div>
              <div className="text-xs text-zinc-500">Boutique en ligne</div>
            </div>
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <ShoppingCart className="w-5 h-5" />
            <span data-testid="cart-count">{cartCount}</span>
          </div>
        </div>
      </header>

      <MemberRedirectDialog product={redirectProduct} onClose={() => setRedirectProduct(null)} />
      <PublicDrops />
      <section className="max-w-6xl mx-auto px-6 py-12">
        <MemberStoreBanner />
        {/* Campagne ÉTÉ — Reconquête KAIZEN (expirée le 1er sept. 2026 → masquée automatiquement) */}
        {SUMMER_PROMO_ACTIVE && (
        <div className="mb-8 rounded-2xl overflow-hidden border-2 border-[#D90429] bg-gradient-to-br from-[#1a0508] via-zinc-900 to-zinc-950 relative" data-testid="summer-campaign-banner">
          <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 85% 20%, #c9a961 0%, transparent 45%)" }} />
          <div className="relative p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-5">
            <div className="flex-1 min-w-0 text-center sm:text-left">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#c9a961] font-bold mb-2">
                <Flame className="w-3.5 h-3.5" /> Collection Été 2026
              </div>
              <h2 className="text-2xl sm:text-4xl font-black uppercase tracking-tight leading-none text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                Reconquête <span style={{ color: "#D90429" }}>estivale</span>
              </h2>
              <p className="text-zinc-300 text-sm mt-2 max-w-md">
                Équipe-toi pour l&apos;été : <span className="text-[#c9a961] font-bold">-15%</span> sur toute la boutique avec le code ci-contre. Du dojo à la maison, garde le rythme KAIZEN.
              </p>
            </div>
            <div className="shrink-0 w-full sm:w-auto">
              <div className="rounded-xl bg-zinc-950 border border-[#c9a961]/40 p-4 text-center">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Code promo été · -15%</div>
                <button
                  onClick={() => { setPromoCode("RECONQUETE15"); toast.success("Code RECONQUETE15 appliqué — -15% !"); }}
                  className="group flex items-center justify-center gap-2 w-full"
                  data-testid="apply-summer-promo"
                >
                  <span className="text-2xl font-mono font-black text-[#c9a961] tracking-wider">RECONQUETE15</span>
                  <Copy className="w-4 h-4 text-zinc-500 group-hover:text-[#c9a961] transition" />
                </button>
                <div className="text-xs text-[#c9a961] mt-1.5">Cliquer pour appliquer</div>
              </div>
            </div>
          </div>
        </div>
        )}

        <h1 className="text-3xl sm:text-5xl font-bold mb-4">
          Boutique <span className="bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">Institut Impact</span>
        </h1>
        <p className="text-zinc-400 max-w-2xl mb-6">
          Équipement officiel, gi BJJ, gants, protections, apparel custom et formations en ligne — directement de Val-d&apos;Or à travers le Canada. Paiement sécurisé Stripe.
        </p>

        {/* Étudiant du mois — bannière */}
        {FEATURED_ACTIVE && (
        <div className="mb-8 rounded-2xl overflow-hidden border border-amber-700/40 bg-gradient-to-r from-amber-950/40 via-zinc-900 to-zinc-950 shadow-lg shadow-amber-900/10" data-testid="featured-student-banner">
          <div className="flex flex-col sm:flex-row gap-5 p-5 sm:p-6 items-center">
            <div className="shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-amber-400 via-amber-600 to-amber-900 flex items-center justify-center text-3xl font-black text-zinc-950 ring-4 ring-amber-700/30">
              {FEATURED_STUDENT.photo_url ? <img src={FEATURED_STUDENT.photo_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${FEATURED_STUDENT.photo_url}` : FEATURED_STUDENT.photo_url} alt={FEATURED_STUDENT.name} className="w-full h-full rounded-full object-cover" data-testid="featured-photo" /> : (FEATURED_STUDENT.name || "").split(" ").map(n => n[0]).join("").slice(0, 2)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                <span className="text-[10px] uppercase tracking-widest text-amber-400 font-semibold">
                  Ambassadeur du mois · {FEATURED_STUDENT.month_label}
                </span>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-amber-100" data-testid="featured-student-name">
                {FEATURED_STUDENT.name}
              </h3>
              <div className="text-xs text-zinc-400 mb-2">
                {FEATURED_STUDENT.age ? `${FEATURED_STUDENT.age} ans · ` : ""}{FEATURED_STUDENT.discipline}{FEATURED_STUDENT.belt ? ` · Ceinture ${FEATURED_STUDENT.belt}` : ""}
              </div>
              <p className="text-sm text-zinc-300 italic leading-relaxed">
                « {FEATURED_STUDENT.testimonial} » <span className="not-italic text-zinc-500">— Kru Ralph</span>
              </p>
            </div>
            <div className="shrink-0 w-full sm:w-auto sm:max-w-xs">
              <div className="rounded-lg bg-zinc-950 border border-amber-600/40 p-3 text-center">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Code promo personnel</div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(FEATURED_STUDENT.promo_code);
                    toast.success(`Code copié : ${FEATURED_STUDENT.promo_code}`);
                  }}
                  className="group flex items-center justify-center gap-2 w-full mt-0.5"
                  data-testid="copy-promo-code"
                >
                  <span className="text-2xl font-mono font-black text-amber-300 tracking-wider">
                    {FEATURED_STUDENT.promo_code}
                  </span>
                  <Copy className="w-4 h-4 text-zinc-500 group-hover:text-amber-400 transition" />
                </button>
                <div className="text-xs text-amber-200 mt-1">{FEATURED_STUDENT.promo_discount}</div>
              <div className="mt-2 text-xs text-zinc-400" data-testid="featured-orders">🛍️ <b className="text-amber-300">{FEATURED_STUDENT.orders_count}</b> commande{FEATURED_STUDENT.orders_count > 1 ? "s" : ""} avec son code ce mois-ci{FEATURED_STUDENT.reward_unlocked ? ` · 🎁 récompense débloquée : ${FEATURED_STUDENT.reward_label || "carte-cadeau dojo"}` : ` · 🎁 récompense dès la 1re commande : ${FEATURED_STUDENT.reward_label || "carte-cadeau dojo"}`}</div>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Search + Categories filter */}
        <div className="mb-6 space-y-3">
          <input
            data-testid="search-input"
            type="text"
            placeholder="🔍 Rechercher un produit..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full max-w-md px-4 py-2.5 rounded-lg bg-zinc-900 border border-zinc-700 text-sm focus:border-amber-500 focus:outline-none"
          />
          <div className="flex flex-wrap gap-2" data-testid="categories-filter">
            {["all", ...Array.from(new Set(products.map(p => p.category).filter(Boolean)))].map(cat => (
              <button
                key={cat}
                data-testid={`cat-${cat}`}
                onClick={() => setActiveCategory(cat)}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${
                  activeCategory === cat
                    ? "bg-amber-500 border-amber-400 text-black font-semibold"
                    : "bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-amber-700"
                }`}
              >
                {cat === "all" ? "Tous" : cat.replace(/_/g, " ")}
                <span className="ml-1 text-[10px] opacity-70">
                  {cat === "all" ? products.length : products.filter(p => p.category === cat).length}
                </span>
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 items-center" data-testid="brands-filter">
            <span className="text-[10px] uppercase tracking-wider text-zinc-600 mr-1">Marques</span>
            {["all", ...Array.from(new Set(products.map(p => p.brand).filter(Boolean)))].map(b => (
              <button
                key={b}
                data-testid={`brand-${b}`}
                onClick={() => setActiveBrand(b)}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${
                  activeBrand === b
                    ? "bg-white border-white text-black font-semibold"
                    : "bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-amber-700"
                }`}
              >
                {b === "all" ? "Toutes" : b}
                <span className="ml-1 text-[10px] opacity-70">
                  {b === "all" ? products.length : products.filter(p => p.brand === b).length}
                </span>
              </button>
            ))}
          </div>
          <button
            data-testid="filter-limited-edition"
            onClick={() => setLimitedOnly(v => !v)}
            className={`text-xs px-3 py-1.5 rounded-full border transition w-fit ${
              limitedOnly
                ? "bg-red-600 border-red-500 text-white font-semibold"
                : "bg-zinc-900 border-red-800/60 text-red-300 hover:border-red-600"
            }`}
          >
            ⭐ Édition limitée
            <span className="ml-1 text-[10px] opacity-80">{products.filter(p => p.limited_edition).length}</span>
          </button>
        </div>
      </section>

      <MascotBand
        className="mb-10"
        title="Porte les couleurs de ta communauté"
        subtitle="KAI, MEI et LANA incarnent l'esprit de l'Institut Impact Élite Martial. Chaque pièce de la boutique soutient ton dojo."
      />

      <section className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products
            .filter(p => activeCategory === "all" || p.category === activeCategory)
            .filter(p => activeBrand === "all" || p.brand === activeBrand)
            .filter(p => !limitedOnly || p.limited_edition)
            .filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.tags || []).some(t => t.toLowerCase().includes(search.toLowerCase())))
            .map((p) => {
            const Icon = TYPE_ICON[p.type] || ShoppingCart;
            return (
              <div key={p.id} className="relative rounded-xl overflow-hidden bg-zinc-900 border border-zinc-800 hover:border-amber-500/50 transition shadow-lg hover:shadow-gold" data-testid={`product-${p.slug}`}>
                {p.limited_edition && (
                  <span className="absolute top-3 left-3 z-10 text-[10px] font-bold uppercase tracking-wider bg-red-600 text-white px-2 py-1 rounded-full shadow-lg" data-testid={`le-badge-${p.slug}`}>
                    ⭐ Édition limitée
                  </span>
                )}
                {p.fulfillment_mode === "preorder_pickup" && (
                  <span className="absolute top-3 right-3 z-10 text-[10px] font-bold uppercase tracking-wider bg-[#0891b2] text-white px-2 py-1 rounded-full shadow-lg" data-testid={`preorder-badge-${p.slug}`}>
                    📦 Précommande · Retrait au dojo
                  </span>
                )}
                {p.image_url && !brokenImgs[p.id] && (
                  <img src={p.image_url} alt={p.name} className="w-full aspect-video object-cover"
                       onError={() => setBrokenImgs((b) => ({ ...b, [p.id]: true }))} />
                )}
                {(!p.image_url || brokenImgs[p.id]) && (
                  <div className="w-full aspect-video bg-gradient-to-br from-zinc-800 to-zinc-900 flex flex-col items-center justify-center gap-2">
                    <span className="text-6xl">{p.image_emoji || "🥋"}</span>
                    {p.brand && <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">{p.brand}</span>}
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-4 h-4 text-amber-400" />
                    <span className="text-[11px] uppercase tracking-wider text-zinc-500">{(p.category || p.type)?.replace(/_/g, " ")}</span>
                    {p.brand && <span className="text-[10px] bg-zinc-800 border border-zinc-700 text-zinc-300 px-1.5 py-0.5 rounded">{p.brand}</span>}
                    {p.is_kit && <span className="ml-auto text-[10px] bg-amber-500/20 border border-amber-600 text-amber-300 px-1.5 py-0.5 rounded-full">KIT</span>}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 line-clamp-1">{p.name}</h3>
                  <p className="text-sm text-zinc-400 mb-4 line-clamp-3 whitespace-pre-line">{p.short_description || p.description}</p>
                  {p.fulfillment_mode === "preorder_pickup" && (
                    <div className="mb-4 text-[11px] text-cyan-300/90 bg-cyan-950/30 border border-cyan-800/40 rounded-lg px-3 py-2 leading-snug" data-testid={`preorder-note-${p.slug}`}>
                      📦 <strong>Précommande</strong> — retrait au dojo (1218 7<sup>e</sup> Rue, Val-d&apos;Or). Aucune livraison directe. Délai 1–2 semaines.
                    </div>
                  )}
                  <div className="flex items-end justify-between">
                    <div>
                      <div className="text-2xl font-bold text-amber-300">
                        {p.price_cad === 0 ? "GRATUIT" : `${p.price_cad.toFixed(2)} $`}
                      </div>
                      {p.is_recurring && <div className="text-xs text-zinc-500">/ mois · CAD</div>}
                      {!p.is_recurring && p.price_cad > 0 && <div className="text-xs text-zinc-500">CAD · taxes en sus</div>}
                    </div>
                    <button
                      data-testid={`add-${p.slug}`}
                      onClick={() => addToCart(p)}
                      className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm font-bold flex items-center gap-1.5 transition"
                    >
                      <ShoppingCart className="w-4 h-4" /> Ajouter
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {cartCount > 0 && (
        <aside className="fixed bottom-0 left-0 right-0 z-40 border-t border-amber-500/30 bg-zinc-950/95 backdrop-blur" data-testid="cart-panel">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="grid md:grid-cols-3 gap-4 items-end">
              <div>
                <div className="text-xs text-zinc-500 mb-1">Votre panier ({cartCount} {cartCount === 1 ? "article" : "articles"})</div>
                <div className="space-y-1 max-h-24 overflow-y-auto">
                  {cartItems.map((p) => (
                    <div key={p.id} className="flex items-center justify-between text-sm">
                      <span className="text-zinc-300">× {cart[p.id]} {p.name.slice(0, 30)}</span>
                      <button onClick={() => removeFromCart(p.id)} className="text-xs text-red-400 ml-2">×</button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <input data-testid="checkout-name" type="text" placeholder="Nom complet"
                         value={name} onChange={(e) => setName(e.target.value)}
                         className="px-3 py-2 rounded bg-zinc-900 border border-zinc-700 text-sm text-white" />
                  <input data-testid="checkout-email" type="email" placeholder="Courriel"
                         value={email} onChange={(e) => setEmail(e.target.value)}
                         className="px-3 py-2 rounded bg-zinc-900 border border-zinc-700 text-sm text-white" />
                </div>
                {multiDisc?.eligible && (
                  <div className="text-[11px] text-emerald-300 bg-emerald-950/30 border border-emerald-800/40 rounded px-3 py-2" data-testid="multi-discipline-banner">
                    🥋 <strong>Rabais multi-disciplines −{multiDisc.discount_pct}%</strong> sur l&apos;équipement{multiDisc.student_name ? ` (${multiDisc.student_name})` : ""} — appliqué automatiquement à la caisse.
                  </div>
                )}
                <input
                  data-testid="checkout-promo"
                  type="text"
                  placeholder="Code promo (ex: KIT-XXXXXX)"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  className={`w-full px-3 py-2 rounded bg-zinc-900 border text-sm text-white placeholder-zinc-600 ${
                    promoCode.trim() ? "border-amber-500 ring-1 ring-amber-500/30" : "border-zinc-700"
                  }`}
                />
                {promoCode.trim() && promoPct === 0 && (
                  <div className="text-[11px] text-amber-300" data-testid="promo-applied-note">
                    ✓ Code « {promoCode.trim()} » appliqué — la réduction s&apos;affiche à la page de paiement.
                  </div>
                )}
                <div className="text-xs text-zinc-500">
                  Sous-total : {subtotal.toFixed(2)} $
                  {promoPct > 0 && (
                    <span className="text-amber-400 font-semibold"> · -{discount.toFixed(2)} $ ({promoPct}%)</span>
                  )}
                  {" · "}TPS : {gst.toFixed(2)} $ · TVQ : {qst.toFixed(2)} $
                </div>
              </div>
              <div>
                <button
                  data-testid="checkout-btn"
                  onClick={checkout}
                  disabled={checkingOut || !email}
                  className="w-full px-6 py-3 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {checkingOut ? <Loader2 className="animate-spin w-4 h-4" /> : null}
                  Payer {total.toFixed(2)} $ CAD
                </button>
              </div>
            </div>
          </div>
        </aside>
      )}
    </div>
  );
}
